import { plainToInstance } from 'class-transformer';

import { instance } from '@/api/config';
import {
  AgreementCompaniesWithoutAgreementListResponse,
  AgreementCompaniesWithoutAgreementQueryDto,
  IAgreementsListResponse,
  IAgreementsQueryDto,
  IAgreementsResponse,
  IAgreementsStatisticsResponse
} from '@/types/agreements';
import { IAnnouncementListResponse, IAnnouncementQueryDto } from '@/types/announcement';
import { IAppsListResponse, IAppsQueryDto, IAppsResponse } from '@/types/apps';
import { ILogin, IMeResponse, IUserLogin } from '@/types/auth';
import type { IBulkChangeFileResponse, IBulkChangeQueryDto } from '@/types/bulk-change';
import { IBulkChangeListResponse, IBulkChangeResponse } from '@/types/bulk-change';
import { IBulkChangeUsersListResponse, IBulkChangeUsersQueryDto } from '@/types/bulk-change-users';
import { IBulkEmailListResponse, IBulkEmailQueryDto, IBulkEmailResponse } from '@/types/bulk-email';
import { ICallForwardingListResponse, ICallForwardingQueryDto, ICallForwardingResponse } from '@/types/call-forwarding';
import { CareerFormFilterQueryDto, CareerFormListResponse, CareerFormResponse } from '@/types/career-form';
import { ChatMessageListQueryDto, ChatMessageResponse, ChatMessagesListResponse } from '@/types/chat-messages-list';
import { ChatBotConversationsListResponse, ChatConversationByIdResponse, ConversationsQueryDto } from '@/types/chatbtot';
import { ICityQueryDto, ISubCityQueryDto } from '@/types/city';
import {
  ICityStatisticsByCityIdListResponse,
  ICityStatisticsByIdListResponse,
  ICityStatisticsListResponse,
  ICityStatisticsResponse
} from '@/types/city-statistics';
import { ICompaniesListResponse, ICompanyQueryDto, ICompanyResponse } from '@/types/companies';
import { ICompanyContactsListResponse, ICompanyContactsResponse } from '@/types/company-contacts';
import { ICompanyFTLListResponse, ICompanyFTLQueryDto } from '@/types/company-ftl';
import { IContainersListResponse, IContainersQueryDto, IContainersResponse } from '@/types/containers';
import { ICounteryListResponse, ICounteryQueryDto, ICounteryResponse } from '@/types/country';
import { ICustomerFormListResponse, ICustomerFormQueryDto, ICustomerFormResponse } from '@/types/customer-form';
import { IDamagePackageListResponse, IDamagePackageQueryDto } from '@/types/damage-package';
import { IDeliveryDocumentDownloadUrlResponse, IDeliveryDocumentListResponse, IDeliveryDocumentQueryDto } from '@/types/delivery-document';
import { DistrictFilterDto, DistrictResponseDto, DistrictResultsDto } from '@/types/districts';
import {
  IDocumentFileDownloadUrlResponse,
  IDocumentFilesListResponse,
  IDocumentFileStatusResponse,
  IDocumentListResponse,
  IDocumentLogQueryDto,
  IDocumentLogsResponse,
  IDocumentQueryDto,
  IDocumentResponse
} from '@/types/document';
import { IDoorListResponse, IDoorQueryDto, IDoorResponse } from '@/types/door';
import { IDriverListResponse, IDriverQueryDto, IDriverResponse } from '@/types/drivers';
import { IIndexRouteListResponse, IIndexRouteQueryDto } from '@/types/index-route';
import { IIndexRouteSessionListResponse, IIndexRouteSessionQueryDto } from '@/types/index-route-session';
import { ICityIndexTotalListResponse } from '@/types/index-total';
import { IInsuranceslListResponse, IInsurancesQueryDto } from '@/types/insurances-list';
import { IInventoryListResponse, IInventoryQueryDto, IInventoryResponse } from '@/types/inventory';
import { IInviteResponse } from '@/types/invite';
import {
  ILossCaseEventsResponse,
  ILossPublicDocumentRequestInfo,
  ILostPackageListResponse,
  ILostPackageLookupResponse,
  ILostPackageQueryDto,
  ILostPackageResponse,
  ILostpackagesFilesListResponse
} from '@/types/lost-packages';
import { IMissionListResponse, IMissionQueryDto, IMissionResponse } from '@/types/mission';
import { MobilizFleetListResponse, MobilizQueryFleetParams } from '@/types/mobiliz-fleet';
import { MobilizQueryVehicleDetailParams, MobilizVehicleDetailListResponse } from '@/types/mobiliz-vehicle-detail';
import { MultiSessionUserQueryParams, MultiSessionUserResponse, MultiSessionUsersResponse } from '@/types/multi-session-users';
import { IPackTypesListResponse, IPackTypesQueryDto, IPackTypesResponse } from '@/types/pack-types';
import {
  IManualPricePayload,
  IOldReturnPackagesListResponse,
  IOldReturnPackagesQueryDto,
  IPackageByCompanyIdStatistics,
  IPackageByVendorIdStatistics,
  IPackageCheckBarcodeResponse,
  IPackageListResponse,
  IPackageMyXDockStatistics,
  IPackagePriceLogsResponse,
  IPackageQueryDto,
  IPackageResponse,
  IZPLPackageResponse
} from '@/types/package';
import { IIPackageAuditListResponse, IPackageAuditQueryDto } from '@/types/package-audit';
import {
  IPackageDesiChange,
  IPackageDesiChangeDownloadResponse,
  IPackageDesiChangeListResponse,
  IPackageDesiChangeQueryDto
} from '@/types/package-desi-change';
import { IPackageIncidentListResponse, IPackageIncidentsQueryDto } from '@/types/package-incidents';
import { IPackageItemListResponse, IPackageItemQueryDto } from '@/types/package-item';
import {
  IPackageRequestListResponse,
  IPackageRequestLogsResponse,
  IPackageRequestQueryDto,
  IPackageRequestResponse
} from '@/types/package-request';
import { IPackageTypeslListResponse, IPackageTypesQueryDto } from '@/types/package-type';
import { IPackageTanzimListResponse, IPackageTanzimQueryDto, IPackageTanzimResponse } from '@/types/packageTanzim';
import {
  CreatePanelNotificationDto,
  IPanelNotificationListResponse,
  IPanelNotificationQueryDto,
  IPanelNotificationResponse,
  PanelNotification,
  UpdatePanelNotificationDto
} from '@/types/panel-notification';
import { IPersonelListResponse, IPersonelQueryDto, IPersonelResponse } from '@/types/personel';
import { IPersonelLogListResponse, IPersonelLogQueryDto, IPersonelLogResponse } from '@/types/personel-log';
import {
  IPointListResponse,
  IPointMapListResponse,
  IPointMapQueryDto,
  IPointMapResponse,
  IPointQueryDto,
  IPointResponse
} from '@/types/point';
import { ICityListResponse, ICityResponse, ISubCityListResponse, ISubCityResponse } from '@/types/province';
import { IRegionContactDetailListResponse, IRegionContactDetailResponse } from '@/types/region-contact-detail';
import { IProvinceCoverageResponse, IRegionDetailListResponse, IRegionDetailQueryDto, IRegionDetailResponse } from '@/types/region-detail';
import { IRegionStatisticsListResponse, IRegionStatisticsQueryDto } from '@/types/region-statistics';
import { RegionTransferTypesListResponse, RegionTransferTypesQueryDto, RegionTransferTypesResponse } from '@/types/region-transfer-types';
import { IReturnPackageListResponse, IReturnPackageQueryDto } from '@/types/return-package';
import { IRouteListResponse, IRouteQueryDto, IRouteResponse } from '@/types/route';
import { IRouteCacheListResponse, IRouteCacheQueryDto, IRouteCacheResponse } from '@/types/route-cache';
import {
  IRouteSessionFileDownloadUrl,
  IRouteSessionFilesListResponse,
  IRouteSessionListResponse,
  IRouteSessionQueryDto,
  IRouteSessionReportResponse,
  IRouteSessionResponse
} from '@/types/route-sessions';
import { SavedReportListResponse, SavedReportQueryDto, SavedReportResponse } from '@/types/saved-reports';
import { SessionInvalidationLogQueryParams, SessionInvalidationLogsResponse } from '@/types/session-invalidation-logs';
import { IQuerySessionPersonale, ISessionPersonaleListResponse, ISessionPersonaleResponse } from '@/types/session-personale';
import { IShipmentListResponse, IShipmentQueryDto, IShipmentRouteWaybillListResponse } from '@/types/shipment-route';
import {
  ISuperAdminHistoryListResponse,
  ISuperAdminHistoryQueryDto,
  ISuperAdminHistoryResponse,
  ISuperAdminStatisticsResponse
} from '@/types/super-admin-history';
import { ISupplierListResponse, ISupplierQueryDto, ISupplierResponse } from '@/types/suppliers';
import {
  TransferVehicleEntryListResponse,
  TransferVehicleEntryLogListResponse,
  TransferVehicleEntryQueryDto,
  TransferVehicleEntryResponse
} from '@/types/transfer-vehicle-entry';
import { IUserListResponse, IUserQueryDto, IUserResponse } from '@/types/user';
import {
  IUserCompanyPermissionListResponse,
  IUserCompanyPermissionResponse,
  IUserCompanypermissionsQueryDto
} from '@/types/user-company-permissions';
import {
  IUserInsurancePermissionListResponse,
  IUserInsurancePermissionResponse,
  IUserInsurancePermissionsQueryDto
} from '@/types/user-insurance-permissions';
import { IUserInviteListResponse, IUserInviteQueryDto } from '@/types/user-invite';
import { IUserOperationsPermissionListResponse, IUserOperationsPermissionResponse } from '@/types/user-operations-permissions';
import {
  IUserVendorPermissionListResponse,
  IUserVendorPermissionResponse,
  IUserVendorpermissionsQueryDto
} from '@/types/user-vendor-permissions';
import {
  IUserXDockPermissionListResponse,
  IUserXDockPermissionResponse,
  IUserXDockpermissionsQueryDto
} from '@/types/user-xdock-permission';
import { IVehicleDepotEntryResponse } from '@/types/vehicle-depot-entry';
import { IVehicleLocationListResponse } from '@/types/vehicle-location';
import { IVehiclePlanQueryDto, IVehiclePlanResponse, IVehliclePlanListResponse } from '@/types/vehicle-plan';
import { IVehicleRequestListResponse, IVehicleRequestQueryDto, IVehicleRequestResponse } from '@/types/vehicle-request';
import {
  IVehicleCategoryListResponse,
  IVehicleCategoryQueryDto,
  IVehicleCategoryResponse,
  IVehicleLookupResponse,
  IVehicleQueryDto,
  IVehicleResponse,
  IVehlicleListResponse
} from '@/types/vehicles';
import { IVendorListResponse, IVendorQueryDto, IVendorResponse } from '@/types/vendor';
import { IWebhooksListResponse, IWebhooksQueryDto, IWebhooksResponse } from '@/types/webhooks';
import { IWorkdayCurrent } from '@/types/workday';
import {
  IXdockDailyOperationsHistoryQueryDto,
  IXdockDailyOperationsHistoryResponse,
  IXdockDailyOperationsQueryDto,
  IXdockDailyOperationsResponse,
  IXdockLivePackagesResponse,
  IXdockLiveStatusResponse,
  XdockLiveSection
} from '@/types/xdock-daily-operations';
import { IXDockStatisticsListResponse, IXDockStatisticsQueryDto, IXDockStatisticsResponse } from '@/types/xdock-statistics';
import { getParams } from '@/utils';

export const login = (data: ILogin) =>
  instance(`auth/login`, {
    method: 'POST',
    body: JSON.stringify(data)
  }).then((data) => plainToInstance(IUserLogin, data));

export const refresh = (token: string) =>
  instance(`auth/refresh`, {
    method: 'POST',
    body: JSON.stringify({
      token: token
    })
  }).then((data) => plainToInstance(IUserLogin, data));

export const me = async () => plainToInstance(IMeResponse, await instance(`auth/me`));

export const newToken = async () => plainToInstance(IUserLogin, await instance(`auth/new`));

export const getPointById = async (id: number | string | undefined | null) =>
  plainToInstance(IPointResponse, await instance(`point/${id}`));

export const getPoint = async (filters?: IPointQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPointListResponse, await instance(`point?${params}`));
};

export const getUserById = async (id: number | string) => plainToInstance(IUserResponse, await instance(`user/${id}`));

export const getUsers = async (filters?: IUserQueryDto) => {
  filters = { ...filters, hidden: 0 };

  const params = getParams({ filters });

  return plainToInstance(IUserListResponse, await instance(`user?${params}`));
};

export const getCompanyById = async (id: number | string | undefined) => plainToInstance(ICompanyResponse, await instance(`company/${id}`));

export const getCompanies = async (filters?: ICompanyQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(ICompaniesListResponse, await instance(`company?${params}`));
};

export const getCityById = async (id: number | string | undefined) => plainToInstance(ICityResponse, await instance(`city/${id}`));

export const getCity = async (filters?: ICityQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ICityListResponse, await instance(`city?${params}`));
};

export const getSubCityById = async (id: number | string | undefined) => plainToInstance(ISubCityResponse, await instance(`subcity/${id}`));

export const getSubCity = async (filters?: ISubCityQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ISubCityListResponse, await instance(`subcity?${params}`));
};

export const getVendorById = async (id: number | string | undefined) => plainToInstance(IVendorResponse, await instance(`vendor/${id}`));

export const getVendors = async (filters?: IVendorQueryDto) => {
  filters = { ...filters, hidden: 0 };
  const params = getParams({ filters });
  return plainToInstance(IVendorListResponse, await instance(`vendor?${params}`));
};

export const getPackageById = async (id: number | string | undefined) => plainToInstance(IPackageResponse, instance(`package/${id}`));

export const getPackages = async (filters?: IPackageQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackageListResponse, await instance(`package?${params}`));
};

export const getPackagesStatistics = async (companyIds: number[] | undefined) => {
  return plainToInstance(IPackageByCompanyIdStatistics, await instance(`package/companies/${companyIds?.join(',')}/statistics`));
};

export const getPackagesMyCompaniesStatistics = async () => {
  return plainToInstance(IPackageByCompanyIdStatistics, await instance(`package/my-companies/statistics`));
};

export const getVendorPackagesStatistics = async (vendorIds: number[] | undefined) => {
  return plainToInstance(IPackageByVendorIdStatistics, await instance(`package/vendors/${vendorIds?.join(',')}/statistics`));
};

export const getPackagesMyVendorStatistics = async () => {
  return plainToInstance(IPackageByVendorIdStatistics, await instance(`package/my-vendors/statistics`));
};

export const getXdockPackagesStatistics = async (vendorIds: number[] | undefined) => {
  return plainToInstance(IPackageMyXDockStatistics, await instance(`package/xdock/${vendorIds?.join(',')}/statistics`));
};

export const getPackagesMyXDockStatistics = async () => {
  return plainToInstance(IPackageMyXDockStatistics, await instance(`package/my-xdock/statistics`));
};

export const getShipmentRoutes = async (filters?: IShipmentQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IShipmentListResponse, await instance(`shipmentRoute?${params}`));
};

export const getPointMapById = async (id: number | string) => plainToInstance(IPointMapResponse, await instance(`pointMap/${id}`));

export const getRouteById = async (id: number | string | undefined) => plainToInstance(IRouteResponse, await instance(`route/${id}`));

export const getRoutes = async (filters?: IRouteQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IRouteListResponse, await instance(`route?${params}`));
};

export const getPackageIncidetsById = async (filters?: IPackageIncidentsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackageIncidentListResponse, await instance(`packageIncidents?${params}`));
};

export const getRegionDetailByRouteId = async (id: number | string | undefined) =>
  plainToInstance(IRegionDetailListResponse, await instance(`regionDetail?routeId=${id}`));

export const getRegionDetailById = async (id: number | string | undefined) =>
  plainToInstance(IRegionDetailResponse, await instance(`regionDetail/${id}`));

export const getRegionDetail = async (filters?: IRegionDetailQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IRegionDetailListResponse, await instance(`regionDetail?${params}`));
};

/** 81 il × bölge dağılımı: atanmamış iller + çakışmalar (cityapi 30 Eki 2026) */
export const getRegionProvinceCoverage = async (): Promise<IProvinceCoverageResponse> => await instance(`regionDetail/province-coverage`);

export const getRegionContactDetailByRegionId = async (id: number | string | undefined) =>
  plainToInstance(IRegionContactDetailListResponse, await instance(`regionContactDetail?region_id=${id}&isActive=1`));

export const getRegionContactDetailById = async (id: number | string | undefined) =>
  plainToInstance(IRegionContactDetailResponse, await instance(`regionContactDetail/${id}`));

export const getVehicleLocationByVehicleId = async (id: number | string) =>
  plainToInstance(IVehicleLocationListResponse, await instance(`vehicleLocation?vehicleId=${id}`));

export const getRouteSessionsById = async (id: number | string | undefined) =>
  plainToInstance(IRouteSessionResponse, await instance(`routeSessions/${id}`));

export const getRouteSessions = async (filters?: IRouteSessionQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(IRouteSessionListResponse, await instance(`routeSessions?${params}`));
};

export interface RouteSessionSnapshotData {
  id: string;
  routeSessionId: number;
  workday: number;
  routeId?: number;
  vehicleId?: number;
  startAt?: string;
  endAt?: string;
  startKmRaw?: number;
  endKmRaw?: number;
  doneKmRaw?: number;
  startKmCanbus?: number;
  endKmCanbus?: number;
  doneKmCanbus?: number;
  reportDistanceKm?: number;
  startTotalPackagesCount?: number;
  endTotalPackagesCount?: number;
  startTotalDesi?: number;
  endTotalDesi?: number;
  startDeliveredPackagesCount?: number;
  endDeliveredPackagesCount?: number;
  startRemainingPackagesCount?: number;
  endRemainingPackagesCount?: number;
  kmStartRecordedAt?: string;
  kmEndRecordedAt?: string;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  packageSnapshotStartAt?: string;
  packageSnapshotEndAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RouteSessionSnapshotResponse {
  results: RouteSessionSnapshotData[];
  page: number;
  limit: number;
}

export const getRouteSessionSnapshotById = async (
  id: number | string,
  workday?: number,
  page = 0,
  limit = 10
): Promise<RouteSessionSnapshotResponse> => {
  const queryParams = new URLSearchParams();

  if (workday !== undefined && Number.isFinite(workday)) {
    queryParams.set('workday', String(workday));
  }

  queryParams.set('page', String(page));
  queryParams.set('limit', String(limit));

  const query = queryParams.toString();
  return instance(`routeSessions/${id}/snapshot${query ? `?${query}` : ''}`);
};

export const getVehicleById = async (id: number | string | undefined) =>
  plainToInstance(IVehicleResponse, await instance(`vehicles/${id}`));

/** Plakayla araç ara (varyasyonlu tam eşleşme); yoksa found=false, eksikse incomplete=true */
export const getVehicleLookup = async (plate: string) =>
  plainToInstance(IVehicleLookupResponse, await instance(`vehicles/lookup/${encodeURIComponent(plate.trim())}`));

export const getVehicles = async (filters?: IVehicleQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IVehlicleListResponse, await instance(`vehicles?${params}`));
};

export const getDrivers = async (filters?: IDriverQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IDriverListResponse, await instance(`drivers?${params}`));
};

export const getDriverById = async (id: number | string | undefined) => plainToInstance(IDriverResponse, await instance(`drivers/${id}`));

export const getVehicleCategoryById = async (id: number | string | undefined) =>
  plainToInstance(IVehicleCategoryResponse, await instance(`vehicles/categories/${id}`));

export const getPackageAudit = async (filters?: IPackageAuditQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IIPackageAuditListResponse, await instance(`packageAudit?${params}`));
};

export const getPackageWayBillBySessionId = async (id: number | string | undefined) =>
  plainToInstance(IShipmentRouteWaybillListResponse, await instance(`shipmentRoute/statistics/${id}/waybill`));

export const getPackageWayBillVendorBySessionId = async (id: number | string | undefined) =>
  plainToInstance(IShipmentRouteWaybillListResponse, await instance(`shipmentRoute/statistics/${id}/waybillVendor`));

export const getPackageWayBillCompanyBySessionId = async (id: number | string | undefined) =>
  plainToInstance(IShipmentRouteWaybillListResponse, await instance(`shipmentRoute/statistics/${id}/waybillCompany`));

export const getCityStatistics = async () => plainToInstance(ICityStatisticsListResponse, await instance(`cityStatistics`));

export const getCityStatisticsById = async (id: number | string | undefined) =>
  plainToInstance(ICityStatisticsResponse, await instance(`cityStatistics/${id}`));

export const getShipmentRouteStatisticsById = async (id: number | string | undefined) =>
  plainToInstance(ICityStatisticsByIdListResponse, await instance(`shipmentRoute/statistics/${id}`));

export const getShipmentRouteStatisticsByCityId = async (sessionId: number | string | undefined, id: number | string) =>
  plainToInstance(ICityStatisticsByCityIdListResponse, await instance(`shipmentRoute/statistics/${sessionId}/${id}`));

export const getCityIndexTotal = async () => plainToInstance(ICityIndexTotalListResponse, await instance(`indexTotal`));

// XDock Statistics
export const getXDockStatistics = async (filters?: IXDockStatisticsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IXDockStatisticsListResponse, await instance(`xdockStatistics?${params}`));
};

export const getXDockStatisticsById = async (id: number | string | undefined) =>
  plainToInstance(IXDockStatisticsResponse, await instance(`xdockStatistics/${id}`));

export const getCompanyFTL = async (filters?: ICompanyFTLQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ICompanyFTLListResponse, await instance(`company/ftl?${params}`));
};

export const getPackageItem = async (filters?: IPackageItemQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackageItemListResponse, await instance(`packageItems?${params}`));
};

export const getIndexRouteSessions = async (filters?: IIndexRouteSessionQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IIndexRouteSessionListResponse, await instance(`indexRouteSessions?${params}`));
};

export const getIndexRouteSessionsOlusturuldu = async (filters?: IIndexRouteSessionQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IIndexRouteSessionListResponse, await instance(`indexRouteSessionsOlusturuldu?${params}`));
};

export const getIndexRoute = async (filters?: IIndexRouteQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IIndexRouteListResponse, await instance(`indexRoute?${params}`));
};

export const getInventory = async (filters?: IInventoryQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IInventoryListResponse, await instance(`inventory?${params}`));
};

export const getSessionPersonale = async (filters?: IQuerySessionPersonale) => {
  const params = getParams({ filters });
  return plainToInstance(ISessionPersonaleListResponse, await instance(`sessionPersonale?${params}`));
};

export const getDeliveryDocument = async (filters?: IDeliveryDocumentQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IDeliveryDocumentListResponse, await instance(`deliveryDocument?${params}`));
};

export const getDeliveryDocumentDownloadUrl = async (id: number | string) =>
  plainToInstance(IDeliveryDocumentDownloadUrlResponse, await instance(`deliveryDocument/${id}/download-url`));

export const getInventoryById = async (id: number | string | undefined) =>
  plainToInstance(IInventoryResponse, await instance(`inventory/${id}`));

export const getWorkDay = async () => plainToInstance(IWorkdayCurrent, await instance(`workDay/current`));

export const getWorkDayFromDate = async (date: string) =>
  plainToInstance(IWorkdayCurrent, await instance(`workDay/from-date?date=${date}`));

export const getSessionPersonaleById = async (id: number | string) =>
  plainToInstance(ISessionPersonaleResponse, await instance(`sessionPersonale/${id}`));

export const getrouteSessionFilesById = async (id: number | string | undefined) =>
  plainToInstance(IRouteSessionFilesListResponse, await instance(`routeSessions/${id}/upload`));

export const getLostPackagesFilesById = async (id: number | string | undefined) =>
  plainToInstance(ILostpackagesFilesListResponse, await instance(`lostPackages/${id}/upload`));

export const getInsurancesList = async (filters?: IInsurancesQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IInsuranceslListResponse, await instance(`insurancesList?${params}`));
};

export const getPackageType = async (filters?: IPackageTypesQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackageTypeslListResponse, await instance(`packType?${params}`));
};

export const getPointMap = async (filters?: IPointMapQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPointMapListResponse, await instance(`pointMap?${params}`));
};

export const getCountries = async (filters?: ICounteryQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ICounteryListResponse, await instance(`country?${params}`));
};

export const getCounteryById = async (id: number | string) => plainToInstance(ICounteryResponse, await instance(`country/${id}`));

export const getCities = async (filters?: ICounteryQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ICounteryListResponse, await instance(`city?${params}`));
};

export const getCitiesById = async (id: number | string) => plainToInstance(ICounteryResponse, await instance(`city/${id}`));

export const getPackTypes = async (filters?: IPackTypesQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackTypesListResponse, await instance(`packType?${params}`));
};

export const getPackTypesById = async (id: number | string) => plainToInstance(IPackTypesResponse, await instance(`packType/${id}`));

export const getVehicleCategories = async (filters?: IVehicleCategoryQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IVehicleCategoryListResponse, await instance(`vehicles/categories?${params}`));
};

export const getVehiclePlan = async (filters?: IVehiclePlanQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IVehliclePlanListResponse, await instance(`vehiclePlan?${params}`));
};

export const getVehiclePlanById = async (id: string | undefined) =>
  plainToInstance(IVehiclePlanResponse, await instance(`vehiclePlan/${id}`));

export const getVehicleDepotEntry = async (workday: number) =>
  plainToInstance(IVehicleDepotEntryResponse, await instance(`vehiclePlan/depot/entry?workday=${workday}`));

export const getReturnPackage = async (filters?: IReturnPackageQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IReturnPackageListResponse, await instance(`returnPackage?${params}`));
};

export const getReturnPackageFiles = async (returnPackageId: string) => {
  return instance(`returnPackage/${returnPackageId}/files`) as Promise<
    {
      id: number;
      package_id: number;
      return_package_id: string;
      file_id: string;
      file_url: string;
      type: string;
      date: string;
      status: number;
    }[]
  >;
};

export const getDamagePackages = async (filters?: IDamagePackageQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IDamagePackageListResponse, await instance(`damagePackage?${params}`));
};

export const getDamagePackageFiles = async (damagePackageId: string) => {
  return instance(`damagePackage/${damagePackageId}/files`) as Promise<
    {
      id: number;
      package_id: number;
      damage_package_id: string;
      file_id: string;
      file_url: string;
      type: string;
      date: string;
      status: number;
    }[]
  >;
};

export const getPackageZplById = async (id: number) => plainToInstance(IZPLPackageResponse, await instance(`package/${id}/zpl`));

export const getPackageZplByBarcode = async (id: string) =>
  plainToInstance(IZPLPackageResponse, await instance(`package/${id}/zplBarcode`));

export const getBulkChangeById = async (id: string | number | null) =>
  plainToInstance(IBulkChangeResponse, await instance(`bulkChange/${id}`));

export const getBulkChange = async (filters?: IBulkChangeQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IBulkChangeListResponse, await instance(`bulkChange?${params}`));
};

export const getBulkChangeFileById = async (id: string | number | null): Promise<IBulkChangeFileResponse> =>
  await instance(`bulkChange/${id}/file`);

export const getVehicleRequestById = async (id: number | string | undefined) =>
  plainToInstance(IVehicleRequestResponse, await instance(`requestVehicle/${id}`));

export const getVehicleRequest = async (filters?: IVehicleRequestQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IVehicleRequestListResponse, await instance(`requestVehicle?${params}`));
};

export const getVehicleRequestStats = async () => {
  return await instance(`requestVehicle/stats`);
};

export const getVehicleRequestLogs = async (id: string) => {
  return await instance(`requestVehicle/${id}/logs`);
};

// ── Paket Alım Talebi ──
export const getPackageRequestById = async (id: number | string | undefined) =>
  plainToInstance(IPackageRequestResponse, await instance(`package-request/${id}`));

export const getPackageRequest = async (filters?: IPackageRequestQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackageRequestListResponse, await instance(`package-request?${params}`));
};

export const getPackageRequestLogs = async (id: string) => {
  return plainToInstance(IPackageRequestLogsResponse, await instance(`package-request/${id}/logs`));
};

export const getSupplierById = async (id: string | undefined) => plainToInstance(ISupplierResponse, await instance(`supplier/${id}`));

export const getSuppliers = async (filters?: ISupplierQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ISupplierListResponse, await instance(`supplier?${params}`));
};

export const getContainersById = async (id: string | undefined) => plainToInstance(IContainersResponse, await instance(`containers/${id}`));

export const getContainers = async (filters?: IContainersQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IContainersListResponse, await instance(`containers?${params}`));
};

export const getRouteSessionsFileDownloadLinkById = async (id: number | string | undefined, uploadId: number | undefined) =>
  plainToInstance(IRouteSessionFileDownloadUrl, await instance(`routeSessions/${id}/upload/${uploadId}/download`));

export const getLostPackagesFileDownloadLinkById = async (id: number | string | undefined, uploadId: number | undefined) =>
  plainToInstance(IRouteSessionFileDownloadUrl, await instance(`lostPackages/${id}/upload/${uploadId}/download`));

export const getMissionById = async (id: string | undefined) => plainToInstance(IMissionResponse, await instance(`mission/${id}`));

export const getMission = async (filters?: IMissionQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IMissionListResponse, await instance(`mission?${params}`));
};

export const getDoorById = async (id: string | undefined) => plainToInstance(IDoorResponse, await instance(`gate/${id}`));

export const getDoor = async (filters?: IDoorQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IDoorListResponse, await instance(`gate?${params}`));
};

export const getPersonelById = async (id: string | undefined) => plainToInstance(IPersonelResponse, await instance(`personel/${id}`));

export const getPersonel = async (filters?: IPersonelQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPersonelListResponse, await instance(`personel?${params}`));
};

export const getPersonelLogById = async (id: string | undefined) =>
  plainToInstance(IPersonelLogResponse, await instance(`personelLog/${id}`));

export const getPersonelLog = async (filters?: IPersonelLogQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPersonelLogListResponse, await instance(`personelLog?${params}`));
};

export const getLostPackage = async (filters?: ILostPackageQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ILostPackageListResponse, await instance(`lostPackages?${params}`));
};

export const getLostPackageById = async (id: number | string | undefined) =>
  plainToInstance(ILostPackageResponse, await instance(`lostPackages/${id}`));

/** Hesapsız belge yükleme: token'lı talep bilgisi (kimlik doğrulaması yok) */
export const getPublicLossDocumentRequest = async (token: string) =>
  plainToInstance(ILossPublicDocumentRequestInfo, await instance(`lostPackages/public/document-requests/${encodeURIComponent(token)}`));

/** Kayıp dosyası İşlem Geçmişi — dosyaya özel lossCaseEvents koleksiyonundan (genel activities değil) */
export const getLostPackageEvents = async (id: number | string | undefined) =>
  plainToInstance(ILossCaseEventsResponse, await instance(`lostPackages/${id}/events`));

export const getLostPackageLookup = async (barcode: string) =>
  plainToInstance(ILostPackageLookupResponse, await instance(`lostPackages/lookup?barcode=${encodeURIComponent(barcode)}`));

export const getCompanyContacts = async (filters?: ILostPackageQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(ICompanyContactsListResponse, await instance(`companyContacts?${params}`));
};

export const getCompanyContactsById = async (id: number | string | undefined) =>
  plainToInstance(ICompanyContactsResponse, await instance(`companyContacts/${id}`));

export const getApps = async (filters?: IAppsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IAppsListResponse, await instance(`apps?${params}`));
};

export const getAppsById = async (id: string | undefined) => plainToInstance(IAppsResponse, await instance(`apps/${id}`));

export const getCaclcPriceById = async (id: string | undefined) =>
  plainToInstance(IPackageResponse, await instance(`package/${id}/calcPrice`));

export const getReCalcRouteById = async (id: string | undefined) => await instance(`package/${id}/recalcroute`);

// ── Elle fiyat yönetimi (özel / ek km / normal) ──
export const patchPackagePricing = async (id: number | string, body: IManualPricePayload) =>
  plainToInstance(
    IPackageResponse,
    await instance(`package/${id}/pricing`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    })
  );

export const postResetPackagePricing = async (id: number | string, body: { note?: string }) =>
  plainToInstance(
    IPackageResponse,
    await instance(`package/${id}/pricing/reset`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  );

export const getPackagePricingLogs = async (id: number | string): Promise<IPackagePriceLogsResponse> =>
  (await instance(`package/${id}/pricing/logs`)) as IPackagePriceLogsResponse;

export const postBulkRecalcRoute = async (body: {
  dateStart: string;
  dateEnd: string;
  companyId?: number;
  vendorId?: number;
  statuses?: number[];
  /** varış ili id listesi */
  ilIds?: number[];
  /** gönderici ili id listesi */
  fromIlIds?: number[];
}) =>
  await instance(`package/bulk-recalcroute`, {
    method: 'POST',
    body: JSON.stringify(body)
  });

export const postBulkCalcPrice = async (body: { dateStart: string; dateEnd: string; companyIds?: number[]; vendorId?: number }) =>
  await instance(`package/bulk-calcprice`, {
    method: 'POST',
    body: JSON.stringify(body)
  });

export const getUserCompanyPermissions = async (filters?: IUserCompanypermissionsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IUserCompanyPermissionListResponse, await instance(`userCompanyPermissions?${params}`));
};

export const getUserCompanyPermissionsById = async (id: number | undefined | 'new') =>
  plainToInstance(IUserCompanyPermissionResponse, await instance(`userCompanyPermissions/${id}`));

export const getUserVendorPermissions = async (filters?: IUserVendorpermissionsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IUserVendorPermissionListResponse, await instance(`userVendorPermissions?${params}`));
};

export const getUserVendorPermissionsById = async (id: number | undefined | 'new') =>
  plainToInstance(IUserVendorPermissionResponse, await instance(`userVendorPermissions/${id}`));

export const getUserInsurancePermissions = async (filters?: IUserInsurancePermissionsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IUserInsurancePermissionListResponse, await instance(`userInsurencePermissions?${params}`));
};

export const getUserInsurancePermissionsById = async (id: number | undefined | 'new') =>
  plainToInstance(IUserInsurancePermissionResponse, await instance(`userInsurencePermissions/${id}`));

export const getUserOperationPermissions = async (filters?: IUserInsurancePermissionsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IUserOperationsPermissionListResponse, await instance(`userOperationPermissions?${params}`));
};

export const getUserOperationPermissionsById = async (id: number | undefined | 'new') =>
  plainToInstance(IUserOperationsPermissionResponse, await instance(`userOperationPermissions/${id}`));

export const getAnnouncement = async (filters?: IAnnouncementQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IAnnouncementListResponse, await instance(`announcement?${params}`));
};

export const getDocumentById = async (id: string | undefined | 'new') =>
  plainToInstance(IDocumentResponse, await instance(`document/${id}`));

export const getDocument = async (filters?: IDocumentQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IDocumentListResponse, await instance(`document?${params}`));
};

// Document File Upload APIs
export const getDocumentFilesById = async (id: string | number | undefined) =>
  plainToInstance(IDocumentFilesListResponse, await instance(`document/${id}/upload`));

export const getDocumentFileDownloadLinkById = async (id: string | number | undefined, uploadId: number | undefined) =>
  plainToInstance(IDocumentFileDownloadUrlResponse, await instance(`document/${id}/upload/${uploadId}/download`));

export const getDocumentFileStatus = async (id: string | number | undefined, uploadId: number | undefined) =>
  plainToInstance(IDocumentFileStatusResponse, await instance(`document/${id}/upload/${uploadId}/status`));

export const getDocumentLogs = async (filters?: IDocumentLogQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IDocumentLogsResponse, await instance(`document/logs/all?${params}`));
};

export const getUserInviteById = async (id: string | undefined, token?: string | undefined) =>
  plainToInstance(IInviteResponse, await instance(`userInvite/${id}?${token ? `token=${token}` : ''}`));

export const getUserInvite = async (filters?: IUserInviteQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IUserInviteListResponse, await instance(`userInvite?${params}`));
};

export const getUserInviteAcceptById = async (id: string | undefined, token?: string | undefined) =>
  plainToInstance(IInviteResponse, await instance(`userInvite/${id}/newuseraccept?${token ? `token=${token}` : ''}`));

export const getUserXDockPermissions = async (filters?: IUserXDockpermissionsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IUserXDockPermissionListResponse, await instance(`userXdockPermissions?${params}`));
};

export const getUserXDockPermissionsById = async (id: number | undefined | 'new') =>
  plainToInstance(IUserXDockPermissionResponse, await instance(`userXdockPermissions/${id}`));

export const getRouteSessionReportById = async (id: number | undefined) =>
  plainToInstance(IRouteSessionReportResponse, await instance(`routeSessions/${id}/report`));

export const getAgreementsById = async (id: number | string | undefined) =>
  plainToInstance(IAgreementsResponse, await instance(`agreements/${id}`));

export const getAgreements = async (filters?: IAgreementsQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(IAgreementsListResponse, await instance(`agreements?${params}`));
};

export const getWebhooksById = async (id: string | undefined) => plainToInstance(IWebhooksResponse, await instance(`webhooks/${id}`));

export const getWebhooks = async (filters?: IWebhooksQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(IWebhooksListResponse, await instance(`webhooks?${params}`));
};

export const getOldReturnPackages = async (filters?: IOldReturnPackagesQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(IOldReturnPackagesListResponse, await instance(`oldReturnPackages?${params}`));
};

export const getBulkChangeUsers = async (filters?: IBulkChangeUsersQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(IBulkChangeUsersListResponse, await instance(`bulkchange/statistics?${params}`));
};

export const getRouteCacheById = async (id: number | string | undefined) =>
  plainToInstance(IRouteCacheResponse, await instance(`route-cache/${id}`));

export const getRouteCache = async (filters?: IRouteCacheQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IRouteCacheListResponse, await instance(`route-cache?${params}`));
};

export const getCheckShipment = async (barcode: string) => {
  return plainToInstance(IPackageCheckBarcodeResponse, await instance(`package/check-barcode/${barcode}`));
};

export const getCustomerFormById = async (id: string | undefined) =>
  plainToInstance(ICustomerFormResponse, await instance(`customer-form/${id}`));

export const getCustomerForm = async (filters?: ICustomerFormQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(ICustomerFormListResponse, await instance(`customer-form?${params}`));
};

export const getCallForwardingById = async (id: string | undefined) =>
  plainToInstance(ICallForwardingResponse, await instance(`call-forwarding/${id}`));

export const getCallForwarding = async (filters?: ICallForwardingQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(ICallForwardingListResponse, await instance(`call-forwarding?${params}`));
};

export const getPackageTanzim = async (filters?: IPackageTanzimQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackageTanzimListResponse, await instance(`packageTanzim?${params}`));
};

export const getPackageTanzimByPackageId = async (id: number) => {
  return plainToInstance(IPackageTanzimResponse, await instance(`packageTanzim/package/${id}`));
};

export const getPackageDesiChangeByPackageId = async (packageId: number | string | undefined) => {
  return plainToInstance(IPackageDesiChangeListResponse, await instance(`package-desi-change/package/${packageId}`));
};

export const getPackageDesiChangeById = async (id: string) => {
  return plainToInstance(IPackageDesiChange, await instance(`package-desi-change/${id}`));
};

export const getPackageDesiChangeList = async (filters?: IPackageDesiChangeQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPackageDesiChangeListResponse, await instance(`package-desi-change?${params}`));
};

export const getPackageDesiChangeImageDownload = async (id: string, uploadId: string | number) => {
  return plainToInstance(IPackageDesiChangeDownloadResponse, await instance(`package-desi-change/${id}/upload/${uploadId}/download`));
};

// Super Admin History API functions
export const getSuperAdminHistoryList = async (filters?: ISuperAdminHistoryQueryDto) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  }
  return plainToInstance(ISuperAdminHistoryListResponse, await instance(`super-admin-history?${params}`));
};

export const getSuperAdminHistoryById = async (id: string) => {
  return plainToInstance(ISuperAdminHistoryResponse, await instance(`super-admin-history/${id}`));
};

export const getSuperAdminHistoryByPackageId = async (packageId: number, filters?: ISuperAdminHistoryQueryDto) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  }
  return plainToInstance(ISuperAdminHistoryListResponse, await instance(`super-admin-history/package/${packageId}?${params}`));
};

export const getSuperAdminHistoryByUserId = async (userId: number, filters?: ISuperAdminHistoryQueryDto) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  }
  return plainToInstance(ISuperAdminHistoryListResponse, await instance(`super-admin-history/user/${userId}?${params}`));
};

export const getSuperAdminHistoryByBarcode = async (barcode: string, filters?: ISuperAdminHistoryQueryDto) => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  }
  return plainToInstance(ISuperAdminHistoryListResponse, await instance(`super-admin-history/barcode/${barcode}?${params}`));
};

export const getSuperAdminStatistics = async (userId?: string) => {
  const params = userId ? `?userId=${userId}` : '';
  return plainToInstance(ISuperAdminStatisticsResponse, await instance(`super-admin-history/statistics/all${params}`));
};

// Panel Notifications API functions
export const getPanelNotifications = async (filters?: IPanelNotificationQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPanelNotificationListResponse, await instance(`panel-notifications?${params}`));
};

export const getPanelNotificationById = async (id: string) => {
  return plainToInstance(IPanelNotificationResponse, await instance(`panel-notifications/${id}`));
};

export const getActivePanelNotifications = async () => {
  const result = await instance('panel-notifications/active');
  if (result && Array.isArray(result.results)) {
    return {
      results: result.results.map((notification: unknown) => plainToInstance(PanelNotification, notification)),
      page: result.page || 0,
      limit: result.limit || 20,
      total: result.total || 0
    } as IPanelNotificationListResponse;
  }
  return {
    results: [],
    page: 0,
    limit: 20,
    total: 0
  } as IPanelNotificationListResponse;
};

export const getMyPanelNotifications = async (filters?: IPanelNotificationQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IPanelNotificationListResponse, await instance(`panel-notifications/my-notifications?${params}`));
};

/** Duyuru okunmamış sayacı — sayfa sınırı yok, my-notifications ile aynı hedefleme (bildirim sözleşmesi §2.12) */
export const getMyAnnouncementsUnreadCount = async (): Promise<{ unread: number }> =>
  instance('panel-notifications/my-notifications/unread-count');

export const createPanelNotification = async (data: CreatePanelNotificationDto) => {
  return await instance('panel-notifications', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const updatePanelNotification = async (id: string, data: UpdatePanelNotificationDto) => {
  return await instance(`panel-notifications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
};

export const deletePanelNotification = async (id: string) => {
  return await instance(`panel-notifications/${id}`, {
    method: 'DELETE'
  });
};

export const togglePanelNotificationStatus = async (id: string) => {
  return await instance(`panel-notifications/${id}/toggle-status`, {
    method: 'PATCH'
  });
};

export const markNotificationAsRead = async (id: string) => {
  return await instance(`panel-notifications/mark-as-read/${id}`, {
    method: 'PATCH'
  });
};

export const markAllNotificationsAsRead = async () => {
  return await instance('panel-notifications/mark-all-as-read', {
    method: 'PATCH'
  });
};

export const getRegionTransferTypesById = async (id: string | undefined) =>
  plainToInstance(RegionTransferTypesResponse, instance(`region-transfer-types/${id}`));

export const getRegionTransferTypes = async (filters?: RegionTransferTypesQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(RegionTransferTypesListResponse, await instance(`region-transfer-types?${params}`));
};

export const getTransferVehicleEntryById = async (id: number | string | undefined) =>
  plainToInstance(TransferVehicleEntryResponse, await instance(`transfer-vehicle-entry/${id}`));

export const getTransferVehicleEntries = async (filters?: TransferVehicleEntryQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(TransferVehicleEntryListResponse, await instance(`transfer-vehicle-entry?${params}`));
};

export const getTransferVehicleEntryLogs = async (id: string, limit?: number) => {
  const params = limit ? `?limit=${limit}` : '';
  return plainToInstance(TransferVehicleEntryLogListResponse, await instance(`transfer-vehicle-entry/${id}/logs${params}`));
};

export const getRegionStatistics = async (filters?: IRegionStatisticsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IRegionStatisticsListResponse, await instance(`region-statistics?${params}`));
};

export const getXdockDailyOperations = async (filters?: IXdockDailyOperationsQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IXdockDailyOperationsResponse, await instance(`xdock-daily-operations?${params}`));
};

export const getXdockDailyOperationsHistory = async (filters: IXdockDailyOperationsHistoryQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IXdockDailyOperationsHistoryResponse, await instance(`xdock-daily-operations/history?${params}`));
};

export const getXdockLiveStatus = async (xdockId: number) => {
  return plainToInstance(IXdockLiveStatusResponse, await instance(`xdock-daily-operations/live?xdock_id=${xdockId}`));
};

export const getXdockLivePackages = async (filters: { xdock_id: number; section: XdockLiveSection; page?: number; limit?: number }) => {
  const params = getParams({ filters });
  return plainToInstance(IXdockLivePackagesResponse, await instance(`xdock-daily-operations/live/packages?${params}`));
};

export const getChatMessageListById = async (id: number | string) =>
  plainToInstance(ChatMessageResponse, await instance(`chat-messages-list/${id}`));

export const getChatMessageList = async (filters?: ChatMessageListQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(ChatMessagesListResponse, await instance(`chat-messages-list?${params}`));
};

export const getConversationById = async (id: number | string) =>
  plainToInstance(ChatConversationByIdResponse, await instance(`ai-chatbot/conversations/${id}`));

export const getConversations = async (filters?: ConversationsQueryDto) => {
  const params = getParams({ filters });

  return plainToInstance(ChatBotConversationsListResponse, await instance(`ai-chatbot/conversations?${params}`));
};

export const getCareerFormById = async (id: string | undefined) => plainToInstance(CareerFormResponse, await instance(`career-form/${id}`));

export const getCareerForm = async (filters?: CareerFormFilterQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(CareerFormListResponse, await instance(`career-form?${params}`));
};

export const getAgreementsWithoutCompany = async (filters?: AgreementCompaniesWithoutAgreementQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(
    AgreementCompaniesWithoutAgreementListResponse,
    await instance(`agreements/companies-without-agreement?${params}`)
  );
};

export const getAgreementsStatistics = async () => plainToInstance(IAgreementsStatisticsResponse, await instance('agreements/statistics'));

export const getMobilizVehicleDetail = async (filters?: MobilizQueryVehicleDetailParams) => {
  const searchParams = new URLSearchParams(filters as Record<string, string>).toString();
  return plainToInstance(MobilizVehicleDetailListResponse, await instance(`mobiliz/vehicles?${searchParams}`));
};

export const getMobilizFleets = async (filters?: MobilizQueryFleetParams) => {
  const searchParams = new URLSearchParams(filters as Record<string, string>).toString();
  return plainToInstance(MobilizFleetListResponse, await instance(`mobiliz/fleets?${searchParams}`));
};

export const getSavedReportById = async (id: string) => plainToInstance(SavedReportResponse, await instance(`saved-reports/${id}`));

export const getSavedReports = async (filters?: SavedReportQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(SavedReportListResponse, await instance(`saved-reports?${params}`));
};

export const getDistrictById = async (id: number | string | undefined | null) =>
  plainToInstance(DistrictResponseDto, await instance(`districts/${id}`));

export const getDistricts = async (filters?: DistrictFilterDto) => {
  const params = getParams({ filters });
  return plainToInstance(DistrictResultsDto, await instance(`districts?${params}`));
};

export const getDistrictsBySubcityId = async (subcityId: number | string) =>
  plainToInstance(DistrictResultsDto, await instance(`districts/subcity/${subcityId}`));

export const getMultiSessionUsers = async (filters?: MultiSessionUserQueryParams) => {
  const params = getParams({ filters });
  return plainToInstance(MultiSessionUsersResponse, await instance(`multi-session-users?${params}`));
};

export const getMultiSessionUserById = async (id: number | string | undefined) =>
  plainToInstance(MultiSessionUserResponse, await instance(`multi-session-users/${id}`));

export const getSessionInvalidationLogs = async (filters?: SessionInvalidationLogQueryParams) => {
  const params = getParams({ filters });
  return plainToInstance(SessionInvalidationLogsResponse, await instance(`auth/session-invalidation-logs?${params}`));
};

export const getBulkEmails = async (filters?: IBulkEmailQueryDto) => {
  const params = getParams({ filters });
  return plainToInstance(IBulkEmailListResponse, await instance(`bulk-email?${params}`));
};

export const getBulkEmailById = async (id: string | undefined) => plainToInstance(IBulkEmailResponse, await instance(`bulk-email/${id}`));

export * from './distribution-expeditions';
export * from './patch';
export * from './post';
export * from './vehicle-bans';
